/**
 * Sent Messages View - Shows messages sent by/* eslint-disable prettier/prettier, curly */
/**
 * Sent Messages View
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Text,
  Badge,
  Spinner,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  SendRegular,
  AttachRegular,
  MoreHorizontalRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast } from '../../../../configurations/index.config';
import { getErrorMessage } from '../../../../types';
import { TablePagination } from '../../../../components/common';
import { formatRelativeDate, stripHtmlTags } from '../../../../utils/memoUtils';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    border: '1px solid #e1dfdd',
    borderRadius: '4px',
  },
  messageItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e1dfdd',
    cursor: 'pointer',
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  messageIcon: {
    marginRight: '12px',
    fontSize: '20px',
    color: '#008272',
  },
  messageContent: {
    flex: 1,
    minWidth: 0,
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  recipients: {
    fontWeight: '600',
    color: '#323130',
  },
  subject: {
    fontSize: '14px',
    color: '#323130',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  snippet: {
    fontSize: '13px',
    color: '#605e5c',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  messageTime: {
    fontSize: '13px',
    color: '#605e5c',
    marginLeft: '12px',
    whiteSpace: 'nowrap',
  },
  deliveryStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    marginLeft: '12px',
  },
  actionButton: {
    minWidth: 'auto',
    marginLeft: '12px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: '#605e5c',
    minHeight: '300px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
});

interface SentMessage {
  message_id: number;
  thread_id: string;
  subject: string;
  snippet: string;
  sent_at: string;
  has_attachments: boolean;
  attachment_count: number;
  recipients: string;
  recipient_count: number;
  failed_count: number;
}

interface ContextType {
  stats: any;
  mailbox: any;
  refreshStats: () => void;
}

export const SentFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { refreshStats } = useOutletContext<ContextType>();
  const [messages, setMessages] = useState<SentMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    loadSent();
  }, [currentPage, pageSize]);

  const loadSent = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.get('/memo/sent', {
        params: {
          page: currentPage,
          limit: pageSize,
        },
      });

      if (response.data.status === 'success') {
        setMessages(response.data.data.items || []);
        setTotalItems(response.data.data.pagination?.total || 0);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = (messageId: number) => {
    navigate(`/account/memo/message/${messageId}`);
  };

  const formatRecipients = (recipients: string, count: number) => {
    if (!recipients) {
      return `${count} recipient${count !== 1 ? 's' : ''}`;
    }
    const recipientList = recipients.split('; ');
    if (recipientList.length <= 2) {
      return recipients;
    }
    return `${recipientList[0]}, ${recipientList[1]} +${recipientList.length - 2} more`;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading sent messages..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>Sent</Text>
        <Text size={300} style={{ color: '#605e5c' }}>
          {totalItems} message{totalItems !== 1 ? 's' : ''}
        </Text>
      </div>

      <div className={styles.messageList}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <SendRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
            <Text size={400}>No sent messages</Text>
          </div>
        ) : (
          messages.map(message => (
            <div
              key={message.message_id}
              className={styles.messageItem}
              onClick={() => handleMessageClick(message.message_id)}
            >
              <div className={styles.messageIcon}>
                <SendRegular />
              </div>

              <div className={styles.messageContent}>
                <div className={styles.messageHeader}>
                  <Text className={styles.recipients}>
                    To: {formatRecipients(message.recipients, message.recipient_count)}
                  </Text>
                  {message.has_attachments && (
                    <AttachRegular style={{ fontSize: '16px', color: '#605e5c' }} />
                  )}
                  {message.failed_count > 0 && (
                    <Badge size="small" color="danger" appearance="filled">
                      {message.failed_count} failed
                    </Badge>
                  )}
                </div>
                <Text className={styles.subject}>{message.subject}</Text>
                <Text className={styles.snippet}>{stripHtmlTags(message.snippet)}</Text>
              </div>

              <div className={styles.deliveryStatus}>
                {message.failed_count === 0 ? (
                  <>
                    <CheckmarkCircleRegular style={{ color: '#107c10' }} />
                    <Text size={200} style={{ color: '#107c10' }}>
                      Delivered
                    </Text>
                  </>
                ) : (
                  <>
                    <ErrorCircleRegular style={{ color: '#d13438' }} />
                    <Text size={200} style={{ color: '#d13438' }}>
                      Failed
                    </Text>
                  </>
                )}
              </div>

              <Text className={styles.messageTime}>{formatRelativeDate(message.sent_at)}</Text>

              <Menu>
                <MenuTrigger>
                  <Button
                    appearance="subtle"
                    icon={<MoreHorizontalRegular />}
                    className={styles.actionButton}
                    onClick={e => e.stopPropagation()}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem onClick={() => handleMessageClick(message.message_id)}>
                      View details
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ marginTop: '16px' }}>
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={() => {}}
          />
        </div>
      )}
    </div>
  );
};

export default SentFluentUI;
